// utils/tagNames.js
//
// A TAG AS A QUERY VALUE (docs/plans/tags.md §3.7; keepr-api docs/SCHEMA.md
// § Querying tags): what a typed name, alias or path resolves to, and what a
// stored id reads as. Pure, shared by the builders' `tags` field
// (utils/kqlBuilder.js), its chips, and the value check the builders run
// (utils/idValueValidation.js, the `tag` kind — ruled d21).
//
// The API's rules, mirrored so the builder can say them where they are typed:
//   - a 24-hex id is the tag (any kind);
//   - in a LIVE query a name, an alias or a path ("Digestive/Stomach pain")
//     resolves among the collection's (and its ancestors') tags the reader
//     can read, plus the reader's own private tags — compared NFKC and
//     case-insensitively, whitespace collapsed; a path names a tag by its
//     last segment and its nearest ancestors by the ones before it;
//   - ITEM tags resolve by id only (their names are their items' titles,
//     ruled d13);
//   - a STORED query (`collection` mode — pinned filters, rules, checks…)
//     names tags by id; the writer rewrites a name to its id, refuses an
//     ambiguous one, and refuses a private tag outright.

import { normalizeId } from './ids';
import { TAG_SEPARATOR, tagIdOf, tagPathLabel, tagParentIdOf } from './tags';
import { itemTagTitleFor } from './tagTitleMemory';

export const TAG_FIELD_KEY = 'tags';
export const UNKNOWN_TAG_LABEL = 'an unknown tag';

const HEX = /^[0-9a-f]{24}$/i;
export const isTagId = value => HEX.test(String(value ?? '').trim());

/** The API's name comparison (keepr-api tagModel normalizeKey): NFKC, lower case, whitespace collapsed. */
export const normalizeTagName = text => `${text ?? ''}`.normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();

const listOf = value => (Array.isArray(value) ? value.filter(Boolean) : []);

/** The names a tag answers to: its name and its aliases. */
const namesOf = tag => [tag?.name, ...listOf(tag?.aliases)].map(normalizeTagName).filter(Boolean);

/**
 * The tags a typed value names, among `tags` (one owner's or several): by
 * name or alias, or — with `/` in it — by path, the last segment naming the
 * tag and each segment before it the next ancestor up. Every candidate, so a
 * caller can tell "none", "one" and "ambiguous" apart.
 */
export function tagsNamed(text, tags = []) {
	const list = listOf(tags);
	const segments = `${text ?? ''}`.split(TAG_SEPARATOR).map(normalizeTagName).filter(Boolean);
	if (!segments.length) return [];
	const byId = new Map(list.map(tag => [tagIdOf(tag), tag]));
	const last = segments[segments.length - 1];
	return list.filter(tag => {
		if (!namesOf(tag).includes(last)) return false;
		let current = tag;
		for (let i = segments.length - 2; i >= 0; i -= 1) {
			current = byId.get(tagParentIdOf(current));
			if (!current || !namesOf(current).includes(segments[i])) return false;
		}
		return true;
	});
}

const titleOf = (id, itemTitles) => {
	const own = itemTitles instanceof Map ? itemTitles.get(id) : (itemTitles && typeof itemTitles === 'object' ? itemTitles[id] : undefined);
	return own || itemTagTitleFor(id) || null;
};

/**
 * What a stored value reads as, for a `tags` field whose options carry the
 * vocabulary (`tags`), my private tags (`privateTags`) and the item-tag
 * titles the page holds (`itemTitles`): a tag's path, an item tag's title,
 * "an unknown tag" for an id nothing names — or undefined for typed text,
 * which reads as typed.
 */
export function tagValueLabel(field, raw) {
	if (raw === undefined || raw === null || raw === '') return undefined;
	const text = String(raw).trim();
	if (!isTagId(text)) return undefined;
	const id = normalizeId(text);
	const options = field?.options || {};
	const known = [...listOf(options.tags), ...listOf(options.privateTags)].find(tag => tagIdOf(tag) === id);
	if (known) return tagPathLabel(known);
	const item = titleOf(id, options.itemTitles);
	if (item?.title) return item.title;
	return UNKNOWN_TAG_LABEL;
}
